'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Share } from 'lucide-react';
import { Button, IconButton } from '@zillow/constellation-10';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { FavoriteHeartIcon } from '@/src/components/features/listings/FavoriteHeartIcon';
import { cx } from '@/styled-system/css';
import { useShareAction } from '@/src/lib/hooks/useShareAction';
import { useListingCTAs, useListingSaveActions } from '@/src/components/features/property/hooks';
import { useListingActions } from '@/src/components/features/listing-actions';
import {
  SHARE_CLICKSTREAM_CONTEXTS,
  type ShareClickstreamContext,
} from '@/src/components/features/listing-actions/listingActionUtils';
import { ListingActionMenu } from '@/src/components/features/listings/ListingActionMenu';
import { ListingActionCluster } from '@/src/components/features/listings/ListingActionCluster';
import {
  headerActionButton,
  headerCtaButton,
  headerCtaButtonLabel,
  headerIconButton,
  utilityActionButton,
  utilityActionButtonLabel,
} from '@/src/components/features/property/ListingPage/desktopStickyHeader.styles';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';

type ListingHeaderActionsProps = {
  listing: PropertyDetailListing;
  showCTAs?: boolean;
  presentation?: 'responsive' | 'mobileFloating';
  mobileFloatingVariant?: 'onMedia' | 'onSurface';
  testIdSuffix?: string;
  analyticsContext?: ShareClickstreamContext;
  /** Legacy prop kept for back-compat; the new pill layout uses fixed dimensions. */
  size?: 'sm' | 'md';
};

/**
 * ListingHeaderActions — the top-right action cluster on the desktop HDP
 * sticky header. Composes Save / Share icon-with-label buttons and (when
 * `showCTAs` is true) "Book tour" / "Message" pill CTAs.
 */
export const ListingHeaderActions = ({
  listing,
  showCTAs,
  presentation = 'responsive',
  testIdSuffix,
  analyticsContext = SHARE_CLICKSTREAM_CONTEXTS.hdp,
}: ListingHeaderActionsProps) => {
  const t = useTranslations('hdp');
  const favoriteButtonRef = useRef<HTMLButtonElement>(null);
  const { listingSaved, toggleSave, identityResolved } = useListingSaveActions(
    listing,
    favoriteButtonRef,
    analyticsContext,
  );
  const { openShare, shareFormOverlay } = useShareAction(
    listing as ExtendedListing | null | undefined,
    analyticsContext,
  );
  const { listingHidden, toggleHide, openReport } = useListingActions(listing, analyticsContext);
  const { ctas, overlays } = useListingCTAs(listing, analyticsContext.triggerLocation);
  const headerCTAs = showCTAs ? ctas : [];
  const shareLabel = t('share');
  const shareAriaLabel = t('shareThisListing');
  const saveLabel = t('save');
  const saveAriaLabel = listingSaved ? t('removeFromSaved') : t('saveThisListing');
  const getTestId = (testId: string) => (testIdSuffix ? `${testId}-${testIdSuffix}` : testId);

  if (presentation === 'mobileFloating') {
    return (
      <>
        <ListingActionCluster label={t('listingOptions')}>
          <IconButton
            ref={favoriteButtonRef}
            type="button"
            shape="circle"
            title={saveLabel}
            aria-label={saveAriaLabel}
            className={headerIconButton}
            onClick={toggleSave}
            disabled={!identityResolved}
            aria-busy={!identityResolved}
            aria-pressed={listingSaved}
            icon={<FavoriteHeartIcon selected={listingSaved} />}
            data-testid={getTestId('button-save-this-listing')}
          />

          <IconButton
            type="button"
            shape="circle"
            title={shareLabel}
            aria-label={shareAriaLabel}
            className={headerIconButton}
            onClick={openShare}
            icon={<LucideIcon icon={Share} />}
            data-testid={getTestId('button-share-this-listing')}
          />

          <ListingActionMenu
            listingHidden={listingHidden}
            onHide={toggleHide}
            onReport={openReport}
            className={headerIconButton}
            testIdSuffix={testIdSuffix}
          />
        </ListingActionCluster>

        {showCTAs && overlays}
        {shareFormOverlay}
      </>
    );
  }

  const saveIcon = <FavoriteHeartIcon selected={listingSaved} />;
  const shareIcon = <LucideIcon icon={Share} size="sm" />;

  return (
    <>
      {headerCTAs.map((cta) => {
        return (
          <Button.Root
            key={cta.id}
            type="button"
            tone="brand"
            emphasis={cta.emphasis}
            size="sm"
            className={headerCtaButton}
            onClick={cta.onClick}
            disabled={cta.disabled}
            aria-busy={cta.ariaBusy}
            aria-label={cta.ariaLabel}
            title={cta.label}
            data-testid={getTestId(`button-${cta.id === 'contact' ? 'message' : cta.id}`)}
          >
            {cta.icon && <Button.Icon>{cta.icon}</Button.Icon>}
            <Button.Label className={cta.icon ? headerCtaButtonLabel : undefined}>{cta.label}</Button.Label>
          </Button.Root>
        );
      })}

      <Button.Root
        ref={favoriteButtonRef}
        type="button"
        tone="neutral"
        emphasis="bare"
        size="sm"
        className={cx(headerActionButton, utilityActionButton)}
        onClick={toggleSave}
        disabled={!identityResolved}
        aria-busy={!identityResolved}
        aria-pressed={listingSaved}
        aria-label={saveAriaLabel}
        title={saveLabel}
        data-testid={getTestId('button-save-this-listing')}
      >
        <Button.Label className={utilityActionButtonLabel}>{saveLabel}</Button.Label>
        <Button.Icon>{saveIcon}</Button.Icon>
      </Button.Root>

      <Button.Root
        type="button"
        tone="neutral"
        emphasis="bare"
        size="sm"
        className={cx(headerActionButton, utilityActionButton)}
        onClick={openShare}
        aria-label={shareAriaLabel}
        title={shareLabel}
        data-testid={getTestId('button-share-this-listing')}
      >
        <Button.Label className={utilityActionButtonLabel}>{shareLabel}</Button.Label>
        <Button.Icon>{shareIcon}</Button.Icon>
      </Button.Root>

      <ListingActionMenu
        listingHidden={listingHidden}
        onHide={toggleHide}
        onReport={openReport}
        className={headerIconButton}
        testIdSuffix={testIdSuffix}
      />

      {showCTAs && overlays}
      {shareFormOverlay}
    </>
  );
};
